// Single source of truth for commands and their shortcuts. The palette, the help sheet and the
// tinykeys bindings all read from here, so what is shown is exactly what is bound. Labels say how
// many presses a command takes: reversible mailbox changes run on one press ("you can undo"),
// irreversible ones arm first ("press again to confirm"); decided 2026-10-12, Chris.

export type CommandId =
  | "palette.open"
  | "help.shortcuts"
  | "search.focus"
  | "undo.last"
  | "nav.feed"
  | "nav.inbox"
  | "nav.tasks"
  | "nav.brief"
  | "nav.notifications"
  | "nav.work"
  | "nav.learning"
  | "nav.hidden"
  | "nav.recent"
  | "nav.freshStart"
  | "nav.screener"
  | "nav.spend"
  | "nav.vips"
  | "nav.pushSettings"
  | "nav.calendar"
  | "nav.accounts"
  | "nav.settings"
  | "nav.config"
  | "config.history"
  | "nav.audit"
  | "nav.drafts"
  | "nav.answers"
  | "nav.ask"
  | "nav.measure"
  | "verdict.open"
  | "nav.saved"
  | "nav.back"
  | "compose.new"
  | "compose.send"
  | "compose.bold"
  | "compose.italic"
  | "compose.link"
  | "compose.bulletList"
  | "compose.orderedList"
  | "compose.quote"
  | "compose.attach"
  | "compose.signature"
  | "compose.insertAnswer"
  | "thread.reply"
  | "thread.replyAll"
  | "thread.forward"
  | "thread.note"
  | "thread.save"
  | "thread.addEvent"
  | "feed.focus"
  | "feed.nextPass"
  | "feed.dock"
  | "feed.recollect"
  | "card.next"
  | "card.prev"
  | "card.thread"
  | "card.confirm"
  | "card.open"
  | "card.cancel"
  | "card.action1"
  | "card.action2"
  | "card.action3"
  | "card.recommended"
  | "card.send"
  | "card.edit"
  | "card.variant"
  | "card.intent"
  | "card.holdingReply"
  | "card.dismiss"
  | "card.snooze"
  | "card.archive"
  | "card.task"
  | "card.research"
  | "card.makeTime"
  | "card.note"
  | "card.pin"
  | "card.remindDeadline"
  | "card.save"
  | "card.addEvent"
  | "tasks.next"
  | "tasks.prev"
  | "tasks.cancel"
  | "tasks.reopen"
  | "tasks.done"
  | "tasks.snooze"
  | "tasks.drop"
  | "tasks.open"
  | "tasks.add"
  | "tasks.makeTime"
  | "screener.next"
  | "screener.prev"
  | "screener.allow"
  | "screener.screenOut"
  | "screener.cards"
  | "screener.brief"
  | "screener.receipts"
  | "screener.screenOut4"
  | "list.next"
  | "list.prev"
  | "list.open"
  | "list.showNew"
  | "list.archive"
  | "list.labels"
  | "list.cancel"
  | "filter.unread"
  | "filter.starred"
  | "thread.images"
  | "thread.expandAll"
  | "theme.toggle"
  | "accounts.connect"
  | "accounts.syncAll"
  | "session.logout";

export type CommandGroup =
  | "General"
  | "Writing"
  | "Go to"
  | "Feed"
  | "Card"
  | "Tasks"
  | "New senders"
  | "Threads"
  | "Reading"
  | "Accounts";

/** Screen a command belongs to. Commands on different screens may share a key. */
export type CommandContext = "inbox" | "feed" | "tasks" | "screener";

export const COMMAND_CONTEXTS: readonly CommandContext[] = ["inbox", "feed", "tasks", "screener"];

export interface CommandSpec {
  id: CommandId;
  label: string;
  group: CommandGroup;
  /** tinykeys bindings (first one is shown). Empty for palette-only commands. */
  keys: readonly string[];
  /** Fire even while typing in an input (only for modifier chords). */
  allowInInput?: boolean;
  /** Extra words for palette fuzzy search. */
  keywords?: readonly string[];
  /** Screen the command lives on; undefined means every screen. */
  context?: CommandContext;
  /**
   * When the command is active, if it shares its key with another command on the same screen
   * (e.g. Enter confirms only while an action is armed). Shown in the shortcuts sheet.
   */
  when?: string;
  /**
   * Ignore auto-repeated key events: holding a key must never arm and then confirm, nor archive
   * row after row.
   */
  noRepeat?: boolean;
}

/**
 * Every command, in priority order: when several commands share a binding, the first one in this
 * list that has a registered handler on the current screen runs.
 */
export const COMMANDS: readonly CommandSpec[] = [
  {
    id: "palette.open",
    label: "Command palette",
    group: "General",
    keys: ["$mod+k"],
    allowInInput: true,
  },
  {
    id: "search.focus",
    label: "Search mail",
    group: "General",
    keys: ["[Shift]+/"],
    keywords: ["find"],
  },
  {
    id: "help.shortcuts",
    label: "Keyboard shortcuts",
    group: "General",
    keys: ["[Shift]+?"],
    keywords: ["help", "keys"],
  },
  // U0: one key on any card, thread, cleanup row or Set aside row. Writes to the audit log only.
  {
    id: "verdict.open",
    label: "Tell MeeMail what it got wrong…",
    group: "General",
    keys: ["[Shift]+!"],
    noRepeat: true,
    when: "on a card, a thread, a cleanup or a Set aside row",
    keywords: [
      "verdict",
      "wrong",
      "mistake",
      "should have been a card",
      "shouldn't be here",
      "draft needed rewriting",
      "shadow week",
      "feedback",
    ],
  },
  {
    id: "compose.new",
    label: "New message",
    group: "General",
    keys: ["c"],
    noRepeat: true,
    keywords: ["compose", "write", "email", "draft", "send"],
  },
  // The compose sheet (a dialog: single keys there are typing, so these are chords or palette).
  {
    id: "compose.send",
    label: "Send (arms; press again to confirm)",
    group: "Writing",
    keys: ["$mod+Enter"],
    allowInInput: true,
    noRepeat: true,
    when: "while writing a message",
    keywords: ["send", "review", "one last look"],
  },
  {
    id: "compose.bold",
    label: "Bold",
    group: "Writing",
    keys: ["$mod+b"],
    allowInInput: true,
    when: "while writing a message",
  },
  {
    id: "compose.italic",
    label: "Italic",
    group: "Writing",
    keys: ["$mod+i"],
    allowInInput: true,
    when: "while writing a message",
  },
  {
    id: "compose.link",
    label: "Add a link",
    group: "Writing",
    keys: ["$mod+k"],
    when: "in the message text",
    keywords: ["url", "hyperlink"],
  },
  {
    id: "compose.bulletList",
    label: "Bulleted list",
    group: "Writing",
    keys: [],
    keywords: ["bullets", "list"],
  },
  {
    id: "compose.orderedList",
    label: "Numbered list",
    group: "Writing",
    keys: [],
    keywords: ["numbers", "list", "steps"],
  },
  { id: "compose.quote", label: "Quote", group: "Writing", keys: [], keywords: ["blockquote"] },
  {
    id: "compose.attach",
    label: "Attach a file",
    group: "Writing",
    keys: [],
    keywords: ["attachment", "upload", "paperclip", "file"],
  },
  {
    id: "compose.signature",
    label: "Signature on or off",
    group: "Writing",
    keys: [],
    keywords: ["sign off", "gmail signature"],
  },
  {
    id: "compose.insertAnswer",
    label: "Insert a saved answer",
    group: "Writing",
    keys: [],
    keywords: ["answer library", "research", "sources", "saved", "reuse"],
  },
  {
    id: "theme.toggle",
    label: "Switch light / dark",
    group: "General",
    keys: [],
    keywords: ["theme", "dark mode"],
  },
  { id: "nav.feed", label: "Go to Feed", group: "Go to", keys: ["g f"], keywords: ["cards"] },
  {
    id: "nav.saved",
    label: "Go to Saved mail",
    group: "Go to",
    keys: ["g v"],
    keywords: ["saved", "collections", "library", "categories"],
  },
  { id: "nav.inbox", label: "Go to Inbox", group: "Go to", keys: ["g i"] },
  {
    id: "nav.tasks",
    label: "Go to Tasks",
    group: "Go to",
    keys: ["g t"],
    keywords: ["todo", "todoist", "waiting", "replies"],
  },
  {
    id: "nav.brief",
    label: "Open the latest brief",
    group: "Go to",
    keys: ["g b"],
    keywords: ["summary", "digest"],
  },
  {
    id: "nav.notifications",
    label: "Show notifications",
    group: "Go to",
    keys: ["g n"],
    keywords: ["bell", "alerts", "reminders"],
  },
  {
    id: "nav.work",
    label: "Go to Under way",
    group: "Go to",
    keys: ["g w"],
    keywords: ["work", "queue", "in progress", "running"],
  },
  {
    id: "nav.learning",
    label: "Go to Learning",
    group: "Go to",
    keys: ["g l"],
    keywords: ["proposals", "revisions"],
  },
  {
    id: "nav.hidden",
    label: "Go to Set aside",
    group: "Go to",
    keys: ["g h"],
    keywords: ["hidden", "restore", "snoozed", "dismissed"],
  },
  {
    id: "nav.recent",
    label: "Go to Recently done",
    group: "Go to",
    keys: ["g r"],
    keywords: ["put back", "undo", "archived", "labels", "history", "done reviewing", "last week"],
  },
  {
    id: "nav.freshStart",
    label: "Fresh start: set older mail aside",
    group: "Go to",
    keys: [],
    keywords: ["backlog", "old mail", "clean up", "declutter", "catch up", "archive old"],
  },
  {
    id: "nav.screener",
    label: "Go to New senders",
    group: "Go to",
    keys: ["g c"],
    keywords: ["screener", "first-time", "screen out", "allow", "new people"],
  },
  { id: "nav.accounts", label: "Go to Accounts", group: "Go to", keys: ["g a"] },
  {
    id: "nav.settings",
    label: "Go to Settings",
    group: "Go to",
    keys: ["g s"],
    keywords: ["password", "two-step"],
  },
  {
    id: "nav.config",
    label: "Go to How Claude works",
    group: "Go to",
    keys: [],
    keywords: ["policy", "prompt", "recipe", "sources", "configuration", "rules"],
  },
  { id: "nav.audit", label: "Go to Audit log", group: "Go to", keys: [], keywords: ["history"] },
  {
    id: "nav.drafts",
    label: "Go to Drafts",
    group: "Go to",
    keys: ["g d"],
    keywords: ["compose", "messages", "unsent", "waiting to send"],
  },
  {
    id: "nav.answers",
    label: "Go to Saved answers",
    group: "Go to",
    keys: [],
    keywords: ["answer library", "research", "sources", "saved", "reuse", "copy"],
  },
  {
    id: "nav.ask",
    label: "Ask about your mail",
    group: "Go to",
    keys: ["g q"],
    keywords: ["question", "find", "when", "what did", "look up", "flight", "invoice", "answer"],
  },
  {
    id: "nav.measure",
    label: "Go to How MeeMail is doing",
    group: "Go to",
    keys: [],
    keywords: ["stats", "measures", "timings", "verdicts", "shadow week", "accuracy", "speed"],
  },
  {
    id: "nav.spend",
    label: "Go to Spend",
    group: "Go to",
    keys: [],
    keywords: ["budget", "cost", "usage", "claude", "money"],
  },
  {
    id: "nav.vips",
    label: "Go to VIPs",
    group: "Go to",
    keys: [],
    keywords: ["important people", "break through", "priority"],
  },
  {
    id: "nav.pushSettings",
    label: "Go to Notifications",
    group: "Go to",
    keys: [],
    keywords: ["push", "phone", "mobile", "alerts", "quiet hours", "devices", "test"],
  },
  {
    id: "nav.calendar",
    label: "Go to Availability & calendars",
    group: "Go to",
    keys: [],
    keywords: [
      "calendar",
      "working hours",
      "availability",
      "holds",
      "meeting times",
      "google calendar",
    ],
  },
  // Feed screens (board, card, focus).
  { id: "feed.focus", label: "Focus mode", group: "Feed", keys: ["f"], context: "feed" },
  {
    id: "feed.nextPass",
    label: "Start the next round",
    group: "Feed",
    keys: ["n"],
    context: "feed",
    noRepeat: true,
  },
  {
    id: "feed.dock",
    label: "Talk to Claude",
    group: "Feed",
    keys: ["."],
    context: "feed",
    keywords: ["dock", "instruction"],
  },
  {
    id: "feed.recollect",
    label: "Have Claude look again",
    group: "Feed",
    keys: [],
    context: "feed",
    keywords: ["search sources again", "missed", "refresh"],
  },
  // How Claude works: available while that screen is open (its editors register the handler).
  {
    id: "config.history",
    label: "Show document history",
    group: "Feed",
    keys: [],
    keywords: ["versions", "revisions", "revert", "restore", "policy", "prompt", "undo"],
  },
  { id: "card.next", label: "Next card", group: "Card", keys: ["j"], context: "feed" },
  { id: "card.prev", label: "Previous card", group: "Card", keys: ["k"], context: "feed" },
  {
    id: "card.thread",
    label: "Show or hide the email thread",
    group: "Card",
    keys: ["o"],
    context: "feed",
  },
  {
    id: "card.confirm",
    label: "Confirm",
    group: "Card",
    keys: ["Enter"],
    context: "feed",
    when: "while an action is armed",
    noRepeat: true,
  },
  { id: "card.open", label: "Open card", group: "Card", keys: ["Enter"], context: "feed" },
  {
    id: "card.cancel",
    label: "Cancel or close",
    group: "Card",
    keys: ["Escape"],
    context: "feed",
    when: "while an action is armed or the thread is open",
  },
  {
    id: "card.action1",
    label: "First action",
    group: "Card",
    keys: ["1"],
    context: "feed",
    noRepeat: true,
  },
  {
    id: "card.action2",
    label: "Second action",
    group: "Card",
    keys: ["2"],
    context: "feed",
    noRepeat: true,
  },
  {
    id: "card.action3",
    label: "Third action",
    group: "Card",
    keys: ["3"],
    context: "feed",
    noRepeat: true,
  },
  {
    id: "card.recommended",
    label: "Do the highlighted next step (a send arms; press again to confirm)",
    group: "Card",
    keys: ["Space"],
    context: "feed",
    noRepeat: true,
    keywords: ["recommended", "next thing", "yes", "go"],
  },
  {
    id: "card.send",
    label: "Send (arms; press again to confirm)",
    group: "Card",
    keys: ["s"],
    context: "feed",
    noRepeat: true,
    keywords: ["reply", "approve"],
  },
  {
    id: "card.edit",
    label: "Edit the draft (or ask Claude for one)",
    group: "Card",
    keys: ["e"],
    context: "feed",
    keywords: ["draft", "write", "reply"],
  },
  {
    id: "card.variant",
    label: "Switch draft version",
    group: "Card",
    keys: ["v"],
    context: "feed",
  },
  // U4: the draft's versions labelled by intent ("Yes, Thursday works", "Can't make it").
  {
    id: "card.intent",
    label: "Pick how to answer (the draft's choices)",
    group: "Card",
    keys: [],
    context: "feed",
    keywords: ["intent", "yes", "no", "decline", "accept", "choice", "version", "variant"],
  },
  // U5: "Got this, I'll get back to you by Friday" in your voice, as a draft on the card. Sending
  // it is the card's send like any other: it arms, and a second press confirms.
  {
    id: "card.holdingReply",
    label: "Write a holding reply (“Got this, I'll get back to you by …”)",
    group: "Card",
    keys: ["b"],
    context: "feed",
    noRepeat: true,
    keywords: ["holding", "acknowledge", "got this", "get back", "later", "by friday", "reply"],
  },
  {
    id: "card.dismiss",
    label: "Dismiss card",
    group: "Card",
    keys: ["d"],
    context: "feed",
    noRepeat: true,
  },
  {
    id: "card.snooze",
    label: "Snooze card",
    group: "Card",
    keys: ["h"],
    context: "feed",
    noRepeat: true,
    keywords: ["later"],
  },
  {
    id: "card.archive",
    label: "Archive thread (you can undo)",
    group: "Card",
    keys: ["a"],
    context: "feed",
    noRepeat: true,
  },
  {
    id: "card.task",
    label: "Make a task from this card",
    group: "Card",
    keys: ["t"],
    context: "feed",
    noRepeat: true,
    keywords: ["todo", "todoist", "capture", "remind"],
  },
  {
    id: "card.research",
    label: "Ask Claude to research this card",
    group: "Card",
    keys: ["r"],
    context: "feed",
    noRepeat: true,
    keywords: ["research", "look up", "web", "sources", "answer", "find out"],
  },
  {
    id: "card.makeTime",
    label: "Make time for this card…",
    group: "Card",
    keys: ["m"],
    context: "feed",
    noRepeat: true,
    keywords: ["calendar", "focus", "block", "schedule", "time block", "plan", "your day"],
  },
  // U6, Remember for me: all local. A note is private (never sent to Gmail or Claude); a pin
  // keeps the card in view across rounds; a reminder is a task that rings on your clock.
  {
    id: "card.note",
    label: "Write a private note on this card…",
    group: "Card",
    keys: ["Shift+n"],
    context: "feed",
    noRepeat: true,
    keywords: ["note", "memo", "remember", "where I left off", "comment"],
  },
  {
    id: "card.pin",
    label: "Pin or unpin this card",
    group: "Card",
    keys: ["p"],
    context: "feed",
    noRepeat: true,
    keywords: ["pin", "keep", "stick", "keep in view", "remember"],
  },
  {
    id: "card.remindDeadline",
    label: "Remind me the day before the deadline",
    group: "Card",
    keys: ["Shift+r"],
    context: "feed",
    noRepeat: true,
    when: "on a card whose email states a deadline",
    keywords: ["remind", "reminder", "deadline", "due", "day before", "task"],
  },
  // Saved mail and events from email: each opens a dialog; nothing changes until you choose.
  {
    id: "card.save",
    label: "Save this email to a collection…",
    group: "Card",
    keys: ["Shift+s"],
    context: "feed",
    noRepeat: true,
    keywords: ["save", "saved mail", "collection", "category", "keep", "file", "library"],
  },
  {
    id: "card.addEvent",
    label: "Add an event from this email to your calendar…",
    group: "Card",
    keys: ["Shift+c"],
    context: "feed",
    noRepeat: true,
    keywords: ["calendar", "event", "appointment", "add to calendar", "reservation", "booking"],
  },
  // Tasks screen (tasks and "Waiting for a reply").
  { id: "tasks.next", label: "Next task", group: "Tasks", keys: ["j"], context: "tasks" },
  { id: "tasks.prev", label: "Previous task", group: "Tasks", keys: ["k"], context: "tasks" },
  {
    id: "tasks.cancel",
    label: "Close",
    group: "Tasks",
    keys: ["Escape"],
    context: "tasks",
    when: "while snoozing or reading the email",
  },
  {
    id: "tasks.reopen",
    label: "Reopen task",
    group: "Tasks",
    keys: ["x"],
    context: "tasks",
    when: "on a done task",
    noRepeat: true,
  },
  {
    id: "tasks.done",
    label: "Mark done",
    group: "Tasks",
    keys: ["x", "Enter"],
    context: "tasks",
    noRepeat: true,
    keywords: ["complete", "check"],
  },
  {
    id: "tasks.snooze",
    label: "Snooze (or wait longer for a reply)",
    group: "Tasks",
    keys: ["h"],
    context: "tasks",
    noRepeat: true,
    keywords: ["later"],
  },
  {
    id: "tasks.drop",
    label: "Drop task (you can undo)",
    group: "Tasks",
    keys: ["d"],
    context: "tasks",
    noRepeat: true,
    keywords: ["remove", "delete"],
  },
  {
    id: "tasks.open",
    label: "Open the email or card",
    group: "Tasks",
    keys: ["o"],
    context: "tasks",
  },
  {
    id: "tasks.add",
    label: "New task",
    group: "Tasks",
    keys: ["n"],
    context: "tasks",
    keywords: ["add", "create", "capture"],
  },
  {
    id: "tasks.makeTime",
    label: "Make time for this task…",
    group: "Tasks",
    keys: ["m"],
    context: "tasks",
    noRepeat: true,
    keywords: ["calendar", "focus", "block", "schedule", "time block", "plan", "your day"],
  },
  // New senders (the screener): local decisions with undo, never a Gmail change.
  {
    id: "screener.next",
    label: "Next sender",
    group: "New senders",
    keys: ["j"],
    context: "screener",
  },
  {
    id: "screener.prev",
    label: "Previous sender",
    group: "New senders",
    keys: ["k"],
    context: "screener",
  },
  {
    id: "screener.allow",
    label: "Allow this sender",
    group: "New senders",
    keys: ["a"],
    context: "screener",
    noRepeat: true,
    keywords: ["accept", "let in"],
  },
  {
    id: "screener.screenOut",
    label: "Screen out this sender (you can undo)",
    group: "New senders",
    keys: ["x"],
    context: "screener",
    noRepeat: true,
    keywords: ["block", "set aside", "hide"],
  },
  // U8: the screener's yes is a choice. 1–4 pick where this sender's mail goes (with Undo).
  {
    id: "screener.cards",
    label: "Cards: their mail can become a card",
    group: "New senders",
    keys: ["1"],
    context: "screener",
    noRepeat: true,
    keywords: ["needs me", "allow", "destination"],
  },
  {
    id: "screener.brief",
    label: "Brief only: one brief, never a card",
    group: "New senders",
    keys: ["2"],
    context: "screener",
    noRepeat: true,
    keywords: ["newsletter", "reading", "destination", "later"],
  },
  {
    id: "screener.receipts",
    label: "Receipts: searchable, never a card or brief item",
    group: "New senders",
    keys: ["3"],
    context: "screener",
    noRepeat: true,
    keywords: ["receipt", "invoice", "order", "destination"],
  },
  {
    id: "screener.screenOut4",
    label: "Screen out (you can undo)",
    group: "New senders",
    keys: ["4"],
    context: "screener",
    noRepeat: true,
    keywords: ["block", "set aside", "destination"],
  },
  // Inbox and thread screens. Thread actions are reversible, so `e` and the label picker's Apply
  // run at once with Undo; nothing here is armed. Esc closes the label picker before it goes back.
  {
    id: "list.cancel",
    label: "Close the label picker",
    group: "Threads",
    keys: ["Escape"],
    context: "inbox",
    when: "while the label picker is open",
  },
  { id: "nav.back", label: "Back", group: "Go to", keys: ["Escape"] },
  { id: "list.next", label: "Next thread", group: "Threads", keys: ["j"], context: "inbox" },
  { id: "list.prev", label: "Previous thread", group: "Threads", keys: ["k"], context: "inbox" },
  {
    id: "list.open",
    label: "Open thread",
    group: "Threads",
    keys: ["Enter", "o"],
    context: "inbox",
  },
  {
    id: "list.archive",
    label: "Archive, or move back to the inbox (you can undo)",
    group: "Threads",
    keys: ["e"],
    context: "inbox",
    noRepeat: true,
    keywords: ["archive", "unarchive", "inbox", "move back"],
  },
  {
    id: "list.labels",
    label: "Change labels…",
    group: "Threads",
    keys: ["l"],
    context: "inbox",
    keywords: ["label", "tag", "move", "folder"],
  },
  {
    id: "filter.unread",
    label: "Toggle the Unread split",
    group: "Threads",
    keys: ["u"],
    context: "inbox",
  },
  {
    id: "filter.starred",
    label: "Toggle the Starred split",
    group: "Threads",
    keys: ["s"],
    context: "inbox",
    keywords: ["star"],
  },
  {
    id: "list.showNew",
    label: "Show new mail",
    group: "Threads",
    keys: ["n"],
    keywords: ["refresh", "new arrivals", "update"],
    context: "inbox",
  },
  // Answering a thread opens the compose sheet; nothing is sent from these keys.
  {
    id: "thread.reply",
    label: "Reply",
    group: "Threads",
    keys: ["w"],
    context: "inbox",
    noRepeat: true,
    keywords: ["respond", "write back", "answer"],
  },
  {
    id: "thread.replyAll",
    label: "Reply all",
    group: "Threads",
    keys: ["a"],
    context: "inbox",
    noRepeat: true,
    keywords: ["respond", "everyone", "cc"],
  },
  {
    id: "thread.forward",
    label: "Forward",
    group: "Threads",
    keys: ["f"],
    context: "inbox",
    noRepeat: true,
    keywords: ["send on", "pass along", "fwd"],
  },
  {
    id: "thread.note",
    label: "Write a private note on this thread…",
    group: "Threads",
    keys: ["Shift+n"],
    context: "inbox",
    noRepeat: true,
    keywords: ["note", "memo", "remember", "where I left off", "comment"],
  },
  {
    id: "thread.save",
    label: "Save this email to a collection…",
    group: "Threads",
    keys: ["Shift+s"],
    context: "inbox",
    noRepeat: true,
    keywords: ["save", "saved mail", "collection", "category", "keep", "file", "library"],
  },
  {
    id: "thread.addEvent",
    label: "Add an event from this email to your calendar…",
    group: "Threads",
    keys: ["Shift+c"],
    context: "inbox",
    noRepeat: true,
    keywords: ["calendar", "event", "appointment", "add to calendar", "reservation", "booking"],
  },
  {
    id: "thread.images",
    label: "Load remote images",
    group: "Reading",
    keys: ["i"],
    keywords: ["pictures"],
    context: "inbox",
  },
  {
    id: "thread.expandAll",
    label: "Expand all messages",
    group: "Reading",
    keys: [";"],
    keywords: ["open all", "show all"],
    context: "inbox",
  },
  // Global fallbacks that share keys with screen commands come last.
  {
    id: "undo.last",
    label: "Undo last action",
    group: "General",
    keys: ["u"],
    when: "right after an action you can undo",
    noRepeat: true,
  },
  {
    id: "accounts.connect",
    label: "Connect a Google account",
    group: "Accounts",
    keys: [],
    keywords: ["gmail", "add"],
  },
  {
    id: "accounts.syncAll",
    label: "Sync all accounts now",
    group: "Accounts",
    keys: [],
    keywords: ["refresh"],
  },
  { id: "session.logout", label: "Log out", group: "Accounts", keys: [], keywords: ["sign out"] },
];

export const GROUP_ORDER: readonly CommandGroup[] = [
  "General",
  "Writing",
  "Go to",
  "Feed",
  "Card",
  "Tasks",
  "New senders",
  "Threads",
  "Reading",
  "Accounts",
];

/**
 * The `?` sheet: commands with a key, by group, then every keyless command in one list (those run
 * from the palette). Together they hold every command exactly once.
 */
export function helpSections(commands: readonly CommandSpec[] = COMMANDS): {
  groups: { group: CommandGroup; commands: CommandSpec[] }[];
  paletteOnly: CommandSpec[];
} {
  const groups = GROUP_ORDER.map((group) => ({
    group,
    commands: commands.filter((c) => c.group === group && c.keys.length > 0),
  })).filter((g) => g.commands.length > 0);
  const paletteOnly = GROUP_ORDER.flatMap((group) =>
    commands.filter((c) => c.group === group && c.keys.length === 0),
  );
  return { groups, paletteOnly };
}

/** Commands bound to `binding`, in priority order. */
export function commandsFor(binding: string, commands: readonly CommandSpec[] = COMMANDS) {
  return commands.filter((c) => c.keys.includes(binding));
}

export function commandById(id: CommandId): CommandSpec {
  const spec = COMMANDS.find((c) => c.id === id);
  if (!spec) throw new Error(`unknown command ${id}`);
  return spec;
}

/**
 * Bindings in the order tinykeys must see them: multi-key sequences first, so "g s" wins over a
 * bare "s" (tinykeys fires the first complete match it iterates).
 */
export function bindingOrder(commands: readonly CommandSpec[] = COMMANDS): [string, CommandSpec][] {
  const pairs = commands.flatMap((c) => c.keys.map((k) => [k, c] as [string, CommandSpec]));
  const presses = (k: string) => k.trim().split(" ").length;
  return pairs.sort((a, b) => presses(b[0]) - presses(a[0]));
}

const KEY_NAMES: Record<string, string> = {
  escape: "Esc",
  enter: "Enter",
  arrowup: "↑",
  arrowdown: "↓",
  " ": "Space",
};

/** Render one tinykeys binding as display keycaps, e.g. "$mod+k" → ["⌘", "K"] on a Mac. */
export function formatBinding(binding: string, isMac: boolean): string[][] {
  return binding
    .trim()
    .split(" ")
    .map((press) => {
      const parts = press.split(/(?<=\w|\])\+/);
      const key = parts.pop() ?? "";
      const mods = parts
        .filter((p) => !p.startsWith("[")) // optional modifiers are layout details, not shown
        .map((mod) => {
          if (mod === "$mod") return isMac ? "⌘" : "Ctrl";
          if (mod === "Shift") return isMac ? "⇧" : "Shift";
          if (mod === "Alt") return isMac ? "⌥" : "Alt";
          if (mod === "Control") return isMac ? "⌃" : "Ctrl";
          if (mod === "Meta") return isMac ? "⌘" : "Win";
          return mod;
        });
      const name = KEY_NAMES[key.toLowerCase()] ?? (key.length === 1 ? key.toUpperCase() : key);
      return [...mods, name];
    });
}

/** The send keys as one keycap ("⌘↵" on a Mac, "Ctrl ↵" elsewhere), matching the ? sheet. */
export function sendKeysLabel(isMac: boolean): string {
  return isMac ? "⌘↵" : "Ctrl ↵";
}

export function isMacPlatform(): boolean {
  return typeof navigator === "object" && /Mac|iPod|iPhone|iPad/.test(navigator.platform);
}

interface TargetLike {
  tagName?: string;
  isContentEditable?: boolean;
  type?: string;
}

const TEXT_INPUT_TYPES = new Set([
  "text",
  "search",
  "email",
  "password",
  "number",
  "tel",
  "url",
  "date",
  "datetime-local",
  "month",
  "time",
  "week",
  "",
]);

/** True for elements where single-letter keys are typing, not commands. */
export function isTypingTarget(target: TargetLike | null | undefined): boolean {
  if (!target) return false;
  if (target.isContentEditable) return true;
  const tag = target.tagName?.toUpperCase();
  if (tag === "TEXTAREA" || tag === "SELECT") return true;
  if (tag === "INPUT") return TEXT_INPUT_TYPES.has((target.type ?? "").toLowerCase());
  return false;
}

/** True for elements that already do something on Enter (so Enter must not also open a thread). */
export function isActivatableTarget(target: TargetLike | null | undefined): boolean {
  const tag = target?.tagName?.toUpperCase();
  return tag === "BUTTON" || tag === "A" || tag === "SUMMARY" || tag === "INPUT";
}
